import _isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { Button, Modal, Popover, Tooltip } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { getFormatRequestDataTime } from '../../../../../../../../../common/utils/time-lib';
import {
  Segment,
  Target,
  actions as createGameRequestActions,
} from '../../../redux';
import SetProjectTargetPopover from './SetProjectTargetPopover';
import { useDispatch, useSelector } from 'react-redux';
import {
  getIsCreateTargetsSettingModal,
  getCreateGameRequestSelectedTargets,
  getGameDetailSelectedTargets,
  getCreateGameRequestShowSelectedTargetsSettingPopover,
  getGameDetailShowSelectedTargetsSettingPopover,
  getGameLegendTournaments,
  getGameDetailSelectedSetTargetsInfo,
  getCreateGameRequestSelectedSetTargetsInfo,
  getIsTargetsSettingEnabled,
  getTargetNums,
  getIsGameNotStarted,
} from '../../../redux/selectors';
import { generateColor, useEvents } from '../../../utils';
import SegmentSelectionPopover, {
  SegmentEditingView,
} from './SegmentSelectionPopover';
import './index.less';
import { TARGET_LEGEND_COMPETITION_HALF } from '../../../utils/constants';
import moment from 'moment';
import { useEvent, useInterval } from 'react-use';

const selectedTargetsTitle = '已选';
const emptyTargetsTitle = '空';

interface SegmentCellProps {
  onlyDisplay?: boolean;
  targetNums: string[];
  segment: Segment;
  segmentIndex: number;
  onInsertSegment?: (segment: Segment, position: 'top' | 'bottom') => void;
  onUpdateSegment?: (segment: Segment) => void;
  onDeleteSegment?: (segment: Segment) => void;
}
const SegmentCell: React.FC<SegmentCellProps> = ({
  onlyDisplay = false,
  targetNums,
  segment,
  segmentIndex,
  onInsertSegment,
  onUpdateSegment,
  onDeleteSegment,
}) => {
  const segmentType = segment.segmentType;
  switch (segmentType) {
    case 'competition':
      return onlyDisplay ? (
        <SegmentDisplayGameCell
          targetNums={targetNums}
          segment={segment}
          segmentIndex={segmentIndex}
          onInsertSegment={onInsertSegment}
          onUpdateSegment={onUpdateSegment}
        />
      ) : (
        <SegmentGameCell
          targetNums={targetNums}
          segment={segment}
          segmentIndex={segmentIndex}
          onInsertSegment={onInsertSegment}
          onUpdateSegment={onUpdateSegment}
          onDeleteSegment={onDeleteSegment}
        />
      );
    default:
      return (
        <SegmentNormalCell
          segment={segment}
          segmentIndex={segmentIndex}
          targetNums={targetNums}
          onlyDisplay={onlyDisplay}
          onInsertSegment={onInsertSegment}
          onUpdateSegment={onUpdateSegment}
          onDeleteSegment={onDeleteSegment}
        />
      );
  }
};

export default SegmentCell;

interface SegmentGameCellProps {
  targetNums: string[];
  segment: Segment;
  segmentIndex: number;
  onInsertSegment?: (segment: Segment, position: 'top' | 'bottom') => void;
  onUpdateSegment?: (segment: Segment) => void;
  onDeleteSegment?: (segment: Segment) => void;
}
const SegmentGameCell: React.FC<SegmentGameCellProps> = ({
  targetNums,
  segment,
  segmentIndex,
  onInsertSegment,
  onUpdateSegment,
  onDeleteSegment,
}) => {
  const dispatch = useDispatch();
  const [dragStartX, setDragStartX] = React.useState<number>();
  const isCreateTargetsSettingModal = useSelector(
    getIsCreateTargetsSettingModal
  );
  const createGameRequestShowSettingPopover = useSelector(
    getCreateGameRequestShowSelectedTargetsSettingPopover
  );
  const detailShowSelectedTargetsSettingPopover = useSelector(
    getGameDetailShowSelectedTargetsSettingPopover
  );
  const isTargetsSettingEnabled = useSelector(getIsTargetsSettingEnabled);
  const showSettingPopover = isCreateTargetsSettingModal
    ? createGameRequestShowSettingPopover
    : detailShowSelectedTargetsSettingPopover;
  const createGameRequestSelectedTargets = useSelector(
    getCreateGameRequestSelectedTargets
  );
  const gameDetailSelectedTargets = useSelector(getGameDetailSelectedTargets);
  const selectedTargetsObj = isCreateTargetsSettingModal
    ? createGameRequestSelectedTargets
    : gameDetailSelectedTargets;

  const selectedTargets = selectedTargetsObj
    ? selectedTargetsObj[segmentIndex] || []
    : [];

  // 保存每一个元素的ref，并在创建时赋值, 页面刷新也不变
  const [segmentRectRefs, setSegmentRectRefs] = React.useState<any[]>(
    targetNums.map(() => React.createRef())
  );
  const tableComponent = document.getElementById('TargetSetting');

  useEffect(() => {
    if (targetNums.length > 0) {
      setSegmentRectRefs(targetNums.map(() => React.createRef()));
    }
  }, [targetNums]);

  useEffect(() => {
    if (!showSettingPopover) {
      updateSelectedTargets([]);
    }
  }, [dispatch, showSettingPopover]);

  const gameCellRef = React.useRef<any>();

  const updateSelectedTargets = (selectedTargets: string[]) => {
    isCreateTargetsSettingModal
      ? dispatch(
          createGameRequestActions.createGameRequestSelectedTargetsUpdate({
            [segmentIndex]: selectedTargets,
          })
        )
      : dispatch(
          createGameRequestActions.loadGameDetailRequestSelectedTargetsUpdate({
            [segmentIndex]: selectedTargets,
          })
        );
  };

  const onDragStart = (e: any) => {
    if (!isTargetsSettingEnabled) {
      return;
    }

    if (showSettingPopover) {
      isCreateTargetsSettingModal
        ? dispatch(
            createGameRequestActions.createGameRequestShowSelectedTargetsSettingPopover(
              false
            )
          )
        : dispatch(
            createGameRequestActions.loadGameDetailRequestShowSelectedTargetsSettingPopover(
              false
            )
          );
    }

    const scrollLeft = tableComponent?.scrollLeft || 0;
    const newStartX = e.clientX + scrollLeft;

    setDragStartX(newStartX);
  };

  const onDragOver = (e: any) => {
    if (!isTargetsSettingEnabled) {
      return;
    }
    if (!dragStartX) {
      return;
    }
    const currentSelectedTargets = calculateCurrentSelectedTargets(e.clientX);
    updateSelectedTargets(currentSelectedTargets);
    setDragStartX(undefined);
    const checkSelectedTargets = (currentSelectedTargets: string[]) =>
      new Promise<void>((resolve, reject) => {
        // selectedTargets 中存在已经设置过的靶位 (通过selectedTargets过滤出segment.targets中的靶位，如果存在 childrenScope和archeryTournamentRoundSettings，那么就是已经设置过的靶位)
        const selectedTargetsInSegment = segment.targets?.filter((item) =>
          currentSelectedTargets.includes(item.targetNum)
        );
        const selectedTargetsInSegmentWithSetting =
          selectedTargetsInSegment?.filter(
            (item) =>
              item.archeryTournamentRoundSettings &&
              !_isEmpty(item.archeryTournamentRoundSettings)
          );
        const isClick = Math.abs(e.clientX - dragStartX) < 20;

        // 如果selectedTargetsInSegmentWithSetting的长度大于0，那么就弹出确认框
        if (
          selectedTargetsInSegmentWithSetting &&
          selectedTargetsInSegmentWithSetting.length > 0 &&
          !isClick
        ) {
          Modal.confirm({
            title: '确认选择靶位',
            content: `选中的靶位中存在已设置过的靶位，是否要覆盖已有设置？`,
            maskStyle: { zIndex: 9997 },
            zIndex: 9998,
            centered: true,
            onOk: () => {
              resolve();
            },
            onCancel: () => {
              reject();
            },
          });
        } else {
          return resolve();
        }
      });
    checkSelectedTargets(currentSelectedTargets)
      .then(() => {
        if (isCreateTargetsSettingModal) {
          dispatch(
            createGameRequestActions.createGameRequestShowSelectedTargetsSettingPopover(
              true
            )
          );
        } else {
          dispatch(
            createGameRequestActions.loadGameDetailRequestShowSelectedTargetsSettingPopover(
              true
            )
          );
        }
      })
      .catch((error) => {
        updateSelectedTargets([]);
      });
  };

  const calculateCurrentSelectedTargets = (currentX: number): string[] => {
    if (!isTargetsSettingEnabled) {
      return [];
    }

    if (!dragStartX) {
      return [];
    }
    const scrollLeft = tableComponent?.scrollLeft || 0;

    const newStartX = currentX + scrollLeft;
    const left = Math.min(dragStartX, newStartX);
    const right = Math.max(dragStartX, newStartX);
    const selectedTargets: string[] = [];

    if (Math.abs(dragStartX - newStartX) < 5) {
      return [];
    }

    for (let i = 0; i < segmentRectRefs.length; i++) {
      //如果当前rect的位置在left和right之间，那么就是选中的，考虑滚动条的情况
      const rect = segmentRectRefs[i].current.getBoundingClientRect();
      const rectLeft = rect.left + scrollLeft;
      const rectRight = rect.right + scrollLeft;

      if (left <= rectRight && right >= rectLeft) {
        selectedTargets.push(targetNums[i]);
      }
    }

    return selectedTargets;
  };

  const [currentDragX, setCurrentDragX] = React.useState<number>();
  const [scrollSpeed, setScrollSpeed] = React.useState<number>(0);

  useInterval(() => {
    if (!dragStartX || !currentDragX || scrollSpeed === 0) {
      return;
    }
    const currentScrollLeft = tableComponent?.scrollLeft || 0;
    const newScrollLeft = Math.min(
      Math.max(0, currentScrollLeft + scrollSpeed),
      tableComponent?.scrollWidth || 0
    );
    tableComponent?.scrollTo(newScrollLeft, tableComponent.scrollTop);
  }, 100);

  useEffect(() => {
    if (!dragStartX || !currentDragX) {
      return;
    }
    const clientWidth = tableComponent?.clientWidth || 0;
    const detectWidth = clientWidth / 3;
    const maxScrollWidth = clientWidth / 10;
    const draggedDistance =
      currentDragX + (tableComponent?.scrollLeft || 0) - dragStartX;

    if (draggedDistance < 0 && currentDragX < detectWidth) {
      const speedRatio = (detectWidth - currentDragX) / detectWidth;
      setScrollSpeed(-maxScrollWidth * (1 - speedRatio));
    } else if (
      draggedDistance > 0 &&
      currentDragX > clientWidth - detectWidth
    ) {
      const speedRatio = (clientWidth - currentDragX) / detectWidth;
      setScrollSpeed(maxScrollWidth * (1 - speedRatio));
    } else {
      setScrollSpeed(0);
    }
  }, [currentDragX]);

  useEffect(() => {
    (!currentDragX || !dragStartX) && setScrollSpeed(0);
  }, [currentDragX, dragStartX]);

  const onDragMove = (e: any) => {
    if (!isTargetsSettingEnabled) {
      return;
    }

    if (!dragStartX) {
      return;
    }
    setCurrentDragX(e.clientX);
    const selectedTargets = calculateCurrentSelectedTargets(e.clientX);
    updateSelectedTargets(selectedTargets);
  };

  useEvent('mouseup', onDragOver, document);

  return (
    <tr
      onMouseDown={onDragStart}
      onMouseMove={onDragMove}
      style={{ position: 'relative' }}
      ref={gameCellRef}
    >
      <SegmentCellHeader
        onlyDisplay={false}
        segment={segment}
        segmentIndex={segmentIndex}
        onInsertSegment={onInsertSegment}
        onUpdateSegment={onUpdateSegment}
      />
      {segment.targets?.map((item, index) => {
        return (
          <td
            key={'round_' + segment.displayName + '_column_' + index}
            onMouseEnter={() => {
              if (!item.archeryTournamentRoundSettings) {
                return;
              }
              const event = new CustomEvent(
                `SegmentDisplayGameCellTD_${segmentIndex}_${index}`,
                {
                  detail: true,
                }
              );
              document.dispatchEvent(event);
            }}
            onMouseLeave={() => {
              if (!item.archeryTournamentRoundSettings) {
                return;
              }
              const event = new CustomEvent(
                `SegmentDisplayGameCellTD_${segmentIndex}_${index}`,
                {
                  detail: false,
                }
              );
              document.dispatchEvent(event);
            }}
            onClick={(e) => {
              if (!isTargetsSettingEnabled) {
                return;
              }
              e.currentTarget.blur();
              const { archeryTournamentRoundSettings, targetNumChildScope } =
                item || {};

              if (
                archeryTournamentRoundSettings &&
                !_isEmpty(archeryTournamentRoundSettings) &&
                targetNumChildScope &&
                targetNumChildScope.length > 0 &&
                selectedTargets.length === 0
              ) {
                isCreateTargetsSettingModal
                  ? dispatch(
                      createGameRequestActions.createGameRequestSelectedSetTargetsInfo(
                        { selectedItem: item, segmentIndex }
                      )
                    )
                  : dispatch(
                      createGameRequestActions.loadGameDetailRequestSelectedSetTargetsInfo(
                        { selectedItem: item, segmentIndex }
                      )
                    );
              }
            }}
          >
            <div
              className={`TargetSettingTableItem`}
              id={`target_${segmentIndex}_${index}`}
              style={{
                cursor:
                  selectedTargets.includes(item.targetNum) ||
                  item.archeryTournamentRoundSettings
                    ? 'default'
                    : 'pointer',
              }}
              ref={segmentRectRefs[index]}
            >
              <div className="TargetSettingTableItemBox flex flex-middle flex-center">
                <span>{emptyTargetsTitle}</span>
              </div>
            </div>
          </td>
        );
      })}
      {isTargetsSettingEnabled ? (
        <td style={{ width: '0', right: 0, position: 'sticky' }}>
          <Button
            disabled={!isTargetsSettingEnabled}
            onClick={(e) => onDeleteSegment && onDeleteSegment(segment)}
            shape="default"
            danger
            ghost
            style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
            onFocus={(e) => {
              e.currentTarget.blur();
            }}
            icon={<CloseOutlined style={{ color: '#ff0000' }} />}
          />
        </td>
      ) : null}
    </tr>
  );
};

const SegmentDisplayGameCell: React.FC<SegmentGameCellProps> = ({
  segment,
  segmentIndex,
  onInsertSegment,
  onUpdateSegment,
}) => {
  const dispatch = useDispatch();

  const legends: any[] = useSelector(getGameLegendTournaments);
  const isCreateTargetsSettingModal = useSelector(
    getIsCreateTargetsSettingModal
  );
  const createGameRequestSelectedTargets = useSelector(
    getCreateGameRequestSelectedTargets
  );
  const gameDetailSelectedTargets = useSelector(getGameDetailSelectedTargets);
  const selectedTargetsObj = isCreateTargetsSettingModal
    ? createGameRequestSelectedTargets
    : gameDetailSelectedTargets;

  const selectedTargets = selectedTargetsObj
    ? selectedTargetsObj[segmentIndex] || []
    : [];
  const createGameRequestShowSettingPopover = useSelector(
    getCreateGameRequestShowSelectedTargetsSettingPopover
  );
  const detailShowSelectedTargetsSettingPopover = useSelector(
    getGameDetailShowSelectedTargetsSettingPopover
  );
  const gameDetailSelectedSetTargetsInfo = useSelector(
    getGameDetailSelectedSetTargetsInfo
  );
  const createGameRequestSelectedSetTargetsInfo = useSelector(
    getCreateGameRequestSelectedSetTargetsInfo
  );
  const isTargetsSettingEnabled = useSelector(getIsTargetsSettingEnabled);
  const selectedSetTargetsInfo = isCreateTargetsSettingModal
    ? createGameRequestSelectedSetTargetsInfo
    : gameDetailSelectedSetTargetsInfo;
  const showSettingPopover = isCreateTargetsSettingModal
    ? createGameRequestShowSettingPopover
    : detailShowSelectedTargetsSettingPopover;
  let selectedTargetsInfoTitle: any = null;

  if (selectedSetTargetsInfo) {
    const {
      archeryTournamentRoundSettings:
        selectedTargetsInfoArcheryTournamentRoundSettings,
      targetNumChildScope: selectedTargetsTargetNumChildScope,
    }: any = selectedSetTargetsInfo || {};
    const {
      categoryName: selectedTargetsInfoCategoryName,
      optionName: selectedTargetsOptionName,
      competitionHalf: selectedTargetsCompetitionHalf,
      roundName: selectedTargetsRoundName,
    } = selectedTargetsInfoArcheryTournamentRoundSettings || {};
    const selectedTargetsInfoCompetitionHalfString =
      !selectedTargetsCompetitionHalf
        ? ''
        : selectedTargetsCompetitionHalf ===
          TARGET_LEGEND_COMPETITION_HALF.FIRSTHALF
        ? '-上'
        : '-下';
    let selectedTargetsInfoTargetNumberChildScopeString = '';

    selectedTargetsTargetNumChildScope?.forEach((item: string) => {
      selectedTargetsInfoTargetNumberChildScopeString += item;
    });

    selectedTargetsInfoTitle = `${selectedTargetsInfoCategoryName}${selectedTargetsOptionName} · ${selectedTargetsRoundName}${selectedTargetsInfoCompetitionHalfString}(${selectedTargetsInfoTargetNumberChildScopeString}型)`;
  }

  // 将targets按照categoryName和optionName分组
  const groupedTargets = () => {
    const segmentTargets = segment.targets || [];
    if (segmentTargets.length === 0) {
      return [];
    }

    const getTargetTitle = (target: Target) => {
      const { targetNumChildScope, archeryTournamentRoundSettings } =
        target || {};
      let targetNumberChildScopeString = '';

      targetNumChildScope?.forEach((item) => {
        targetNumberChildScopeString += item;
      });
      if (selectedTargets.includes(target.targetNum)) {
        return `${selectedTargetsTitle}(${selectedTargets.length}个靶)`;
      } else if (archeryTournamentRoundSettings) {
        const { competitionHalf }: { competitionHalf: string } =
          archeryTournamentRoundSettings || {};
        const competitionHalfString = !competitionHalf
          ? ''
          : competitionHalf === TARGET_LEGEND_COMPETITION_HALF.FIRSTHALF
          ? '-上'
          : '-下';

        return `${archeryTournamentRoundSettings?.categoryName}${archeryTournamentRoundSettings?.optionName} · ${archeryTournamentRoundSettings?.roundName}${competitionHalfString}(${targetNumberChildScopeString}型)`;
      } else {
        return emptyTargetsTitle;
      }
    };
    const getTargetColor = (target: Target) => {
      if (
        !selectedTargets.includes(target.targetNum) &&
        !target.archeryTournamentRoundSettings
      ) {
        return 'black';
      } else {
        return 'white';
      }
    };

    const getTargetBackgroundColor = (target: Target) => {
      // 如果是选中item 返回蓝色
      if (selectedTargets.includes(target.targetNum)) {
        return 'blue';
      } else if (target.archeryTournamentRoundSettings) {
        return generateColor(
          legends.findIndex(
            (l) =>
              target.archeryTournamentRoundSettings?.categoryName ===
                l.categoryName &&
              target.archeryTournamentRoundSettings?.optionName === l.optionName
          )
        );
      } else {
        return 'rgba(255,255,255,0.9)';
      }
    };

    const resultArray: {
      startIndex: number;
      length: number;
      title: string;
      color: string;
      backgroundColor: string;
      isSelectedTitle: boolean;
    }[] = [];
    let currentGroup: {
      startIndex: number;
      length: number;
      title: string;
      color: string;
      backgroundColor: string;
      isSelectedTitle: boolean;
    } | null = null;

    for (let i = 0; i < segmentTargets.length; i++) {
      const currentItem = segmentTargets[i];

      const currentTitle = getTargetTitle(currentItem);
      const currentColor = getTargetColor(currentItem);
      const currentBackgroundColor = getTargetBackgroundColor(currentItem);

      if (
        currentGroup === null ||
        currentGroup.title !== currentTitle ||
        currentGroup.backgroundColor !== currentBackgroundColor ||
        currentGroup.color !== currentColor
      ) {
        currentGroup = {
          startIndex: i,
          length: 1,
          title: currentTitle,
          backgroundColor: currentBackgroundColor,
          color: currentColor,
          isSelectedTitle: selectedTargetsInfoTitle === currentTitle,
        };
        resultArray.push(currentGroup);
      } else if (currentTitle === emptyTargetsTitle) {
        resultArray.push({
          startIndex: i,
          length: 1,
          title: currentTitle,
          backgroundColor: currentBackgroundColor,
          color: currentColor,
          isSelectedTitle: selectedTargetsInfoTitle === currentTitle,
        });
      } else {
        currentGroup.length++;
      }
    }

    return resultArray;
  };

  return (
    <tr style={{ position: 'relative' }}>
      <SegmentCellHeader
        onlyDisplay={true}
        segment={segment}
        segmentIndex={segmentIndex}
        onInsertSegment={onInsertSegment}
        onUpdateSegment={onUpdateSegment}
      />
      {groupedTargets().map((item, index) => {
        // 用item.length 创建数组遍历
        const cellWidth = Array.from({ length: item.length }).map(
          (_, index) => {
            const cellRect = document
              .getElementById('target_' + index)
              ?.getBoundingClientRect();
            return cellRect?.width || 0;
          }
        );
        const totalCellWidth = cellWidth.reduce((a, b) => a + b, 0);

        return (
          <SetProjectTargetPopover
            key={`SetProjectTargetPopover_column_${index}`}
            isReselectedItem={
              (item.isSelectedTitle ||
                item.title.startsWith(selectedTargetsTitle)) &&
              showSettingPopover
            }
            visible={
              (item.isSelectedTitle ||
                item.title.startsWith(selectedTargetsTitle)) &&
              showSettingPopover
            }
            trigger="click"
            onVisibleChange={(visible) => {
              if (isCreateTargetsSettingModal) {
                dispatch(
                  createGameRequestActions.createGameRequestShowSelectedTargetsSettingPopover(
                    visible
                  )
                );
                !visible &&
                  dispatch(
                    createGameRequestActions.createGameRequestSelectedSetTargetsInfo(
                      null
                    )
                  );
              } else {
                dispatch(
                  createGameRequestActions.loadGameDetailRequestShowSelectedTargetsSettingPopover(
                    visible
                  )
                );
                !visible &&
                  dispatch(
                    createGameRequestActions.loadGameDetailRequestSelectedSetTargetsInfo(
                      null
                    )
                  );
              }
            }}
            onFinishPicker={(value) => {
              const oldTargets = segment.targets || [];
              // 将oldTargets中的targetNum 包含在selectedTargets中的targetNum的target的archeryTournamentRoundSettings和targetNumChildScope更新
              const newTargets: Target[] = oldTargets.map((target) => {
                if (selectedTargets.includes(target.targetNum)) {
                  return {
                    ...target,
                    archeryTournamentRoundSettings:
                      value.archeryTournamentRoundSettings,
                    targetNumChildScope: value.targetNumChildScope,
                  };
                } else {
                  return target;
                }
              });
              const newSegment = {
                ...segment,
                targets: newTargets,
              };
              console.log('newSegment', newSegment);

              onUpdateSegment && onUpdateSegment(newSegment);
            }}
            onDelete={() => {
              if (isCreateTargetsSettingModal) {
                dispatch(
                  createGameRequestActions.selectedCreateSetTargetsDeleted({
                    selectedTargets,
                    segmentIndex,
                  })
                );
              } else {
                dispatch(
                  createGameRequestActions.selectedDetailSetTargetsDeleted({
                    selectedTargets,
                    segmentIndex,
                  })
                );
              }
            }}
          >
            <SegmentDisplayGameCellTD
              item={item}
              segment={segment}
              segmentIndex={segmentIndex}
              index={index}
              totalCellWidth={totalCellWidth}
            />
          </SetProjectTargetPopover>
        );
      })}
      {isTargetsSettingEnabled ? (
        <td style={{ width: '0', right: 0, position: 'sticky' }}>
          <Button
            disabled={!isTargetsSettingEnabled}
            shape="default"
            danger
            ghost
            style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
            onFocus={(e) => {
              e.currentTarget.blur();
            }}
            icon={<CloseOutlined style={{ color: '#ff0000' }} />}
          />
        </td>
      ) : null}
    </tr>
  );
};

interface SegmentDisplayGameCellTDProps {
  segment: Segment;
  segmentIndex: number;
  index: number;
  item: {
    startIndex: number;
    length: number;
    title: string;
    color: string;
    backgroundColor: string;
    isSelectedTitle: boolean;
  };
  totalCellWidth: number;
}
const SegmentDisplayGameCellTD: React.FC<SegmentDisplayGameCellTDProps> = ({
  item,
  segment,
  segmentIndex,
  index,
  totalCellWidth,
}) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  useEvents(
    new Array(item.length)
      .fill(0)
      .map(
        (_, i) =>
          `SegmentDisplayGameCellTD_${segmentIndex}_${
            Math.max(0, item.startIndex) + i
          }`
      ),
    (e: any) => {
      setShowTooltip(e.detail);
    },
    document
  );
  const title = `${item.title}${
    item.title.startsWith(emptyTargetsTitle) ||
    item.title.startsWith(selectedTargetsTitle)
      ? ''
      : `(${item.length}个靶)`
  }`;

  return (
    <td
      key={'round_' + segment.displayName + '_column_' + index}
      colSpan={item.length}
      className="SegmentDisplayGameCell"
    >
      <div
        id={'round_' + segment.displayName + '_column_' + index}
        className="TargetSettingTableItem"
        style={{
          ...(item.title.startsWith(emptyTargetsTitle)
            ? {}
            : { border: 'none' }),
          color: item.color,
          backgroundColor: item.backgroundColor,
        }}
      >
        <Tooltip
          visible={showTooltip}
          overlayInnerStyle={{ textAlign: 'center' }}
          overlayStyle={{ zIndex: 9999 }}
          trigger={'hover'}
          title={title}
          destroyTooltipOnHide
        >
          <div
            className="TargetSettingTableItemBox flex flex-middle flex-center"
            style={{
              width: Math.max(0, totalCellWidth / 10),
              position: 'relative',
            }}
          >
            <div
              className="TargetSettingTableItemBoxText"
              style={{
                position: 'absolute',
                width: (totalCellWidth / 10) * 9,
                left: -(totalCellWidth / 10) * 4,
              }}
            >
              {title}
            </div>
          </div>
        </Tooltip>
      </div>
    </td>
  );
};
interface SegmentNormalCellProps {
  segment: Segment;
  segmentIndex: number;
  targetNums: string[];
  onlyDisplay: boolean;
  onInsertSegment?: (segment: Segment, position: 'top' | 'bottom') => void;
  onUpdateSegment?: (segment: Segment) => void;
  onDeleteSegment?: (segment: Segment) => void;
}
const SegmentNormalCell: React.FC<SegmentNormalCellProps> = ({
  segment,
  segmentIndex,
  targetNums,
  onlyDisplay,
  onInsertSegment,
  onDeleteSegment,
  onUpdateSegment,
}) => {
  const isTargetsSettingEnabled = useSelector(getIsTargetsSettingEnabled);

  return (
    <tr>
      <SegmentCellHeader
        onlyDisplay={onlyDisplay}
        segment={segment}
        segmentIndex={segmentIndex}
        onInsertSegment={onInsertSegment}
        onUpdateSegment={onUpdateSegment}
      />
      <td colSpan={targetNums.length}>
        <div
          className={`TargetSettingTableRound TargetSettingTable${segment.segmentType}`}
        >
          &nbsp;
        </div>
      </td>
      {isTargetsSettingEnabled ? (
        <th style={{ width: '0', right: 0, position: 'sticky' }}>
          <Button
            disabled={!isTargetsSettingEnabled}
            onClick={(e) => onDeleteSegment && onDeleteSegment(segment)}
            shape="default"
            danger
            ghost
            style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
            onFocus={(e) => {
              e.currentTarget.blur();
            }}
            icon={<CloseOutlined style={{ color: '#ff0000' }} />}
          />
        </th>
      ) : null}
    </tr>
  );
};

let tempChangedSegment: any = null;
interface SegmentCellHeaderProps {
  segment: Segment;
  segmentIndex: number;
  onlyDisplay: boolean;
  onInsertSegment?: (segment: Segment, position: 'top' | 'bottom') => void;
  onUpdateSegment?: (segment: Segment) => void;
}
const SegmentCellHeader: React.FC<SegmentCellHeaderProps> = ({
  segment,
  segmentIndex,
  onlyDisplay,
  onUpdateSegment,
  onInsertSegment,
}) => {
  const targetNums = useSelector(getTargetNums);
  const [showHeaderPopover, setShowHeaderPopover] =
    React.useState<boolean>(false);
  const isTargetsSettingEnabled = useSelector(getIsTargetsSettingEnabled);
  const isGameNotStarted = useSelector(getIsGameNotStarted);
  const [selectRoundTypePopup, setSelectRoundTypePopup] = useState<
    undefined | 'top' | 'bottom'
  >();
  const getLastSegmentTime = (position: 'top' | 'bottom') => {
    switch (position) {
      case 'top':
        return moment(segment.startTime)
          .subtract(30, 'minute')
          .format('YYYY-MM-DD HH:mm');
      case 'bottom':
        return moment(segment.endTime).format('YYYY-MM-DD HH:mm');
    }
  };
  const handleSubmit = (currentSegment: any) => {
    console.log(currentSegment);
    console.log(tempChangedSegment);
    if (!tempChangedSegment) {
      onUpdateSegment && onUpdateSegment(currentSegment);
      tempChangedSegment = currentSegment;
    } else {
      onUpdateSegment && onUpdateSegment(tempChangedSegment);
      tempChangedSegment = null;
    }

    setShowHeaderPopover(false);
  };

  return (
    <th style={{ width: 0 }}>
      <Popover
        zIndex={2000}
        placement="right"
        trigger={['click']}
        autoAdjustOverflow={false}
        visible={showHeaderPopover}
        onVisibleChange={(visible) => !visible && setShowHeaderPopover(visible)}
        title={'编辑赛程'}
        content={
          <SegmentEditingView
            segment={segment}
            onSubmit={(currentSegment) => {
              handleSubmit(currentSegment);
            }}
          />
        }
      >
        <div className="TargetSettingTableCellButton">
          {segment.displayName}
          <div className="TargetSettingTableCellButtonDescription">
            {/* segment.startTime segment.endTime,是同一天，格式化成 2022-10-23 10:30-11:30 */}
            {getFormatRequestDataTime(segment.startTime) ===
            getFormatRequestDataTime(segment.endTime)
              ? getFormatRequestDataTime(segment.startTime)
              : ''}
          </div>
          <div className="TargetSettingTableCellButtonDescription">
            {/* segment.startTime segment.endTime,是同一天，格式化成 2022-10-23 10:30-11:30 */}
            {getFormatRequestDataTime(segment.startTime) ===
            getFormatRequestDataTime(segment.endTime)
              ? `${getFormatRequestDataTime(
                  segment.startTime,
                  'HH:mm'
                )} - ${getFormatRequestDataTime(segment.endTime, 'HH:mm')}`
              : `${getFormatRequestDataTime(
                  segment.startTime,
                  'YYYY-MM-DD HH:mm'
                )} - ${getFormatRequestDataTime(
                  segment.endTime,
                  'YYYY-MM-DD HH:mm'
                )}`}
          </div>

          {isGameNotStarted && (
            <SegmentSelectionPopover
              trigger={['click']}
              visible={selectRoundTypePopup !== undefined}
              lastSegmentTime={getLastSegmentTime('top')}
              onFinishPicker={(segment) => {
                const targets =
                  segment.segmentType === 'competition'
                    ? targetNums?.map((targetNum: any) => {
                        return {
                          targetNum,
                        };
                      })
                    : undefined;

                selectRoundTypePopup &&
                  onInsertSegment &&
                  onInsertSegment(
                    {
                      ...segment,
                      targets,
                    },
                    selectRoundTypePopup
                  );

                setSelectRoundTypePopup(undefined);
              }}
              onVisibleChange={(visible) => {
                if (!visible) {
                  setSelectRoundTypePopup(undefined);
                }
              }}
            >
              <div
                className="ActionArea"
                onMouseOver={() => {
                  const maskTable = document.querySelector(
                    '#TargetSettingTableMask'
                  );

                  if (maskTable) {
                    const maskTableActionAreas: any =
                      maskTable.querySelectorAll('.ActionArea');

                    if (
                      maskTableActionAreas &&
                      maskTableActionAreas[segmentIndex]
                    ) {
                      maskTableActionAreas[segmentIndex].style.opacity = 1;
                    }
                  }
                }}
                onMouseOut={() => {
                  const maskTable = document.querySelector(
                    '#TargetSettingTableMask'
                  );

                  if (maskTable) {
                    const maskTableActionAreas: any =
                      maskTable.querySelectorAll('.ActionArea');

                    if (
                      maskTableActionAreas &&
                      maskTableActionAreas[segmentIndex]
                    ) {
                      maskTableActionAreas[segmentIndex].style.opacity = 0;
                    }
                  }
                }}
              >
                {isTargetsSettingEnabled && (
                  <Button
                    size="small"
                    block
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      height: 'auto',
                      flex: 1,
                    }}
                    icon={<PlusOutlined />}
                    onClick={() => setSelectRoundTypePopup('top')}
                  />
                )}
                <Button
                  size="small"
                  block
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: ' white',
                    height: 'auto',
                    flex: 1,
                  }}
                  onClick={() => setShowHeaderPopover(!showHeaderPopover)}
                >
                  编辑
                </Button>
                {isTargetsSettingEnabled && (
                  <Button
                    size="small"
                    block
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: ' white',
                      height: 'auto',
                      flex: 1,
                    }}
                    icon={<PlusOutlined />}
                    onClick={() => setSelectRoundTypePopup('bottom')}
                  />
                )}
              </div>
            </SegmentSelectionPopover>
          )}
        </div>
      </Popover>
    </th>
  );
};
